import React, { useEffect, useState } from "react";
import {
    IonModal, IonContent, IonHeader, IonToolbar, IonTitle, IonFooter,
    IonRow, IonCol, IonButton, IonItem, IonLabel, IonInput, IonSelect,
    IonSelectOption, useIonAlert, IonIcon, IonRadio, IonRadioGroup, IonButtons
} from "@ionic/react";
import {star, addCircleOutline, trashOutline, arrowBack} from "ionicons/icons";
import useStore from "../zustand/useStore";
import {
    actionToCreateEventPurchaseOrder,
    actionToGetMemberPanelEventsByFilter,
    actionToVerifyEventPayment
} from "../apiHelpers/CommonAction";
import {Capacitor} from "@capacitor/core";
import {Browser} from "@capacitor/browser";
import {openIosPaymentPage} from "../lib/paymentPageHelper";

let RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

export default function EventRegistrationModal({
                                                   isOpen,
                                                   onDismiss,
                                                   onSubmit,
                                                   eventRegisterData
                                               }) {

    const [presentAlert] = useIonAlert();
    const [billing, setBilling] = useState({
        entity_name: "",
        gstin: ""
    });
    const [eventsToSelect, setEventsToSelect] = useState([]);
    const [totalPriceToPay, setTotalPriceToPay] = useState('0.00');
    const {userAuthDetail} = useStore();
    const {userInfo} = userAuthDetail;

    useEffect(() => {
        if (isOpen) {
            let eventData = [];
            if (eventRegisterData?.combos?.length) {
                eventRegisterData?.combos?.forEach((eventInCom) => {
                    eventInCom?.events?.forEach((eventIn) => {
                        eventData.push({
                            id: eventIn?.id,
                            event_ids: `${eventIn?.event_id},0`,
                            is_event_combo: false,
                            title: eventIn.title,
                            price_for_ca: eventIn.price_for_ca,
                            price_for_member: eventIn.price_for_member,
                            price_for_other: eventIn.price_for_other,
                        });
                    });
                    eventData.push({
                        id: eventInCom?.id,
                        event_ids: eventInCom?.event_ids,
                        is_event_combo: true,
                        title: eventInCom.title,
                        price_for_ca: eventInCom.price_for_ca,
                        price_for_member: eventInCom.price_for_member,
                        price_for_other: eventInCom.price_for_other,
                    });
                });
                setEventsToSelect([...eventData]);
            } else {
                setEventsToSelect([{
                    id: eventRegisterData?.id,
                    is_event_combo: false,
                    event_ids: `${eventRegisterData?.id},0`,
                    title: eventRegisterData.title,
                    price_for_ca: eventRegisterData.price_for_ca,
                    price_for_member: eventRegisterData.price_for_member,
                    price_for_other: eventRegisterData.price_for_other,
                }]);
            }
        }
    }, [isOpen, eventRegisterData]);

    // Members form
    const emptyMember = () => ({
        name: userInfo?.name,
        email: userInfo?.email,
        mobile_no: userInfo?.phone_number,
        type: "price_for_other",
        icai_membership_no: "",
        event_id: eventRegisterData?.id || null,
        event_ids: `${eventRegisterData?.id},0`,
        is_event_combo: false,
        total_price: 0 // numeric
    });
    const [members, setMembers] = useState([emptyMember()]);

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            setErrors({});
            if (eventRegisterData) {
                setBilling({
                    entity_name:  "",
                    gstin: ""
                });
                setMembers([emptyMember()]);
            } else {
                setBilling({ entity_name: "", gstin: "" });
                setMembers([emptyMember()]);
            }
        }
    }, [isOpen, eventRegisterData]);

    const setBillingField = (k, v) => setBilling(prev => ({ ...prev, [k]: v }));

    // helper: compute price for a given event id and member type
    const getPriceForMember = (eventId, type) => {
        if (!eventId) return 0;
        const ev = eventsToSelect.find(e => Number(e.id) === Number(eventId));
        if (!ev) return 0;
        const t = type || "price_for_other";
        const price = ev?.[t] ? ev[t] : ev.price_for_other ;
        // ensure numeric
        return Number(price || 0);
    };

    const setMemberField = (idx, k, v) => {
        if (k === 'event_id') {
            // update event_id, is_event_combo, and total_price based on selected event
            setMembers(prev => {
                const clone = [...prev];
                const updated = { ...clone[idx], [k]: v };
                // find event
                const selectedEvent = eventsToSelect.find(ev => Number(ev.id) === Number(v));
                updated.is_event_combo = !!selectedEvent?.is_event_combo;
                updated.event_ids = selectedEvent?.event_ids;
                updated.total_price = getPriceForMember(v, updated.type);
                clone[idx] = updated;
                return clone;
            });
        } else if (k === 'type') {
            // type change should update total_price based on same event
            setMembers(prev => {
                const clone = [...prev];
                const updated = { ...clone[idx], [k]: v };
                updated.total_price = getPriceForMember(updated.event_id, v);
                clone[idx] = updated;
                return clone;
            });
        } else {
            setMembers(prev => {
                const clone = [...prev];
                clone[idx] = { ...clone[idx], [k]: v };
                return clone;
            });
        }
    };

    const addMember = () => {
        // when adding, pre-fill event_id with default event if exists and compute price
        const m = emptyMember();
        const defaultEventId = eventRegisterData?.id || (eventsToSelect[0] && eventsToSelect[0].id) || null;
        m.event_id = defaultEventId;
        m.is_event_combo = !!eventsToSelect.find(ev => Number(ev.id) === Number(defaultEventId))?.is_event_combo;
        m.total_price = getPriceForMember(defaultEventId, m.type);
        setMembers(prev => [...prev, m]);
    };

    const removeMember = (idx) =>
        setMembers(prev => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));

    const validate = () => {
        const e = {};
        if (!billing.entity_name) e.entity_name = "Entity / Legal name is required";

        members.forEach((m, i) => {
            if (!m.name) (e[`m_${i}_name`] = "Name is required");
            if (!m.email) (e[`m_${i}_email`] = "Email is required");
            if (!m.mobile_no) (e[`m_${i}_mobile`] = "Mobile No is required");
            if (!m.type) (e[`m_${i}_type`] = "Type is required");
            if (!m.event_id) (e[`m_${i}_event`] = "Select an event");
        });

        return e;
    };

    // recalc final total whenever members or eventsToSelect change
    useEffect(() => {
        // Ensure each member has up-to-date total_price in case eventsToSelect changed
        setMembers(prev => {
            const updated = prev.map(m => {
                const computed = getPriceForMember(m.event_id, m.type);
                if (Number(m.total_price) !== Number(computed)) {
                    return { ...m, total_price: computed };
                }
                return m;
            });
            return updated;
        });
        // compute sum
        const sum = members.reduce((acc, m) => acc + Number(m.total_price || 0), 0);
        setTotalPriceToPay(Number(sum).toFixed(2));
    }, [eventsToSelect, members.length]); // members.length to trigger when member added/removed

    // also watch members deeply for changes to recompute final total
    useEffect(() => {
        const sum = members.reduce((acc, m) => acc + Number(m.total_price || 0), 0);
        setTotalPriceToPay(Number(sum).toFixed(2));
    }, [members]);

    const handleEventPurchase = async (payload) => {
        try {
            const { data } = await actionToCreateEventPurchaseOrder(payload);
            const { order, breakdown } = data;

            if (Capacitor.getPlatform() === "ios") {
                await openIosPaymentPage(order.id,'event');
                return;
            }

            const { total } = breakdown;

            // 2️⃣ Open Razorpay checkout
            const options = {
                key: RAZORPAY_KEY_ID,
                amount: Math.round(total * 100), // in paise
                currency: "INR",
                name: "AICAS Event",
                description: `Payment for Event`,
                order_id: order.id,
                prefill: {
                    name: userInfo.name,
                    email: userInfo.email,
                    contact: userInfo.phone_number,
                },
                handler: async (response) => {
                    try {
                        // 3️⃣ Verify payment
                        await actionToVerifyEventPayment(response);
                        onDismiss?.();
                        actionToGetMemberPanelEventsByFilter(true);
                        presentAlert({ message: "Payment successful! You are registered for the event.", buttons: ["OK"] });
                    } catch (error) {
                        console.error("Payment verification error:", error);
                        alert("Error verifying payment. Please contact support.");
                    }
                },
                theme: { color: "#3399cc" },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (err) {
            console.error("Error creating event order:", err);
            alert("Unable to initiate payment. Please try again later.");
        }
    };

    const handleSubmit = async () => {
        const v = validate();
        setErrors(v);
        if (Object.keys(v).length) {
            presentAlert({ message: "Please fill the required fields.", buttons: ["OK"] });
            return;
        }

        const payload = {
            entity_name: billing.entity_name,
            gstin: billing.gstin || null,
            members: members.map(m => ({
                name: m.name,
                email: m.email,
                mobile_no: m.mobile_no,
                type: m.type,
                icai_membership_no: m.icai_membership_no || null,
                event_id: Number(m.event_id),
                is_event_combo: m.is_event_combo,
                event_ids: m.event_ids,
                total_price: m.total_price ? Number(m.total_price) : 0
            }))
        };

        try {
            await onSubmit?.(payload);
            handleEventPurchase(payload)
        } catch (err) {
            presentAlert({ message: "Something went wrong while saving.", buttons: ["OK"] });
        }
    };

    return (
        <IonModal
            className="sheet_modal_header"
            isOpen={isOpen}
            onDidDismiss={onDismiss}
            breakpoints={[0, 1]}
            initialBreakpoint={1}
        >
            <IonHeader className="job_detail_sheet_header sheet_modal_header">
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton className={"close_job_post_button"} onClick={onDismiss}>
                            <IonIcon icon={arrowBack}/>
                        </IonButton>
                    </IonButtons>
                    <IonTitle>Event Registration</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding bottom-2 no_padding_top">
                {/* Billing Section */}
                <div className="rfp_forn_heading_field">
                    <IonLabel>
                        Billing Details
                    </IonLabel>
                </div>

                <div className="rfp_submit_form_item">
                    <IonLabel>
                        Entity / Trade / Legal name
                        <IonIcon icon={star} className="required-star" />
                    </IonLabel>
                    <IonInput
                        className="rfp_form_input"
                        placeholder="Enter entity name"
                        value={billing.entity_name}
                        onIonInput={(e) => setBillingField("entity_name", e.detail.value)}
                    />
                    {errors.entity_name && <p className="error-text">{errors.entity_name}</p>}
                </div>

                <div className="rfp_submit_form_item">
                    <IonLabel>GSTIN</IonLabel>
                    <IonInput
                        className="rfp_form_input"
                        placeholder="Enter GSTIN (optional)"
                        value={billing.gstin}
                        onIonInput={(e) => setBillingField("gstin", e.detail.value?.toUpperCase())}
                    />
                </div>
                <hr className="rfp_member_divider" />
                {/* Members Section */}
                <div className="rfp_forn_heading_field">
                    <IonLabel>Member(s)</IonLabel>
                </div>

                {members.map((m, idx) => (
                    <div key={idx} className="rfp_member_block">
                        <div className="rfp_submit_form_item">
                            <IonLabel>
                                Name<IonIcon icon={star} className="required-star" />
                            </IonLabel>
                            <IonInput
                                className="rfp_form_input"
                                placeholder="Member name"
                                value={m.name}
                                onIonInput={(e) => setMemberField(idx, "name", e.detail.value)}
                            />
                            {errors[`m_${idx}_name`] && <p className="error-text">{errors[`m_${idx}_name`]}</p>}
                        </div>

                        <div className="rfp_submit_form_item">
                            <IonLabel>
                                Email Id<IonIcon icon={star} className="required-star" />
                            </IonLabel>
                            <IonInput
                                className="rfp_form_input"
                                type="email"
                                placeholder="email@example.com"
                                value={m.email}
                                onIonInput={(e) => setMemberField(idx, "email", e.detail.value)}
                            />
                            {errors[`m_${idx}_email`] && <p className="error-text">{errors[`m_${idx}_email`]}</p>}
                        </div>

                        <div className="rfp_submit_form_item">
                            <IonLabel>
                                Mob No<IonIcon icon={star} className="required-star" />
                            </IonLabel>
                            <IonInput
                                className="rfp_form_input"
                                type="tel"
                                inputmode="numeric"
                                placeholder="10-digit mobile number"
                                value={m.mobile_no}
                                onIonInput={(e) => setMemberField(idx, "mobile_no", e.detail.value)}
                            />
                            {errors[`m_${idx}_mobile`] && <p className="error-text">{errors[`m_${idx}_mobile`]}</p>}
                        </div>

                        <div className="rfp_submit_form_item">
                            <IonLabel>
                                Type<IonIcon icon={star} className="required-star" />
                            </IonLabel>
                            <IonSelect
                                interface="popover"
                                className="rfp_form_input"
                                value={m.type}
                                onIonChange={(e) => setMemberField(idx, "type", e.detail.value)}
                            >
                                <IonSelectOption value="price_for_member">AICAS Member</IonSelectOption>
                                <IonSelectOption value="price_for_ca">CA</IonSelectOption>
                                <IonSelectOption value="price_for_other">Others</IonSelectOption>
                            </IonSelect>
                            {errors[`m_${idx}_type`] && <p className="error-text">{errors[`m_${idx}_type`]}</p>}
                        </div>

                        <div className="rfp_submit_form_item">
                            <IonLabel>ICAI Membership No</IonLabel>
                            <IonInput
                                className="rfp_form_input"
                                placeholder="(Optional)"
                                value={m.icai_membership_no}
                                onIonInput={(e) => setMemberField(idx, "icai_membership_no", e.detail.value)}
                            />
                        </div>

                        <div className="rfp_submit_form_item">
                            <IonLabel>
                                Event<IonIcon icon={star} className="required-star" />
                            </IonLabel>

                            <IonRadioGroup
                                value={m.event_id}
                                onIonChange={(e) => setMemberField(idx, "event_id", e.detail.value)}
                            >
                                {eventsToSelect.map((ev) => (
                                    <IonItem className="rfp_form_input" key={ev.id} lines="none">
                                        <IonLabel>
                                            {ev.title} (AICAS Member - ₹{ev?.price_for_member ?? 0}, CA - ₹{ev?.price_for_ca ?? 0}, Others - ₹{ev?.price_for_other ?? 0} incl. GST )
                                        </IonLabel>
                                        <IonRadio slot="start" value={ev.id} />
                                    </IonItem>
                                ))}
                            </IonRadioGroup>

                            {errors[`m_${idx}_event`] && (
                                <p className="error-text">{errors[`m_${idx}_event`]}</p>
                            )}
                        </div>

                        <div className="rfp_submit_form_item">
                            <IonLabel>
                                Member Price
                            </IonLabel>
                            <div style={{ fontWeight: 600 }}>
                                ₹{Number(m.total_price || 0).toFixed(2)}
                            </div>
                        </div>

                        {(idx > 0) ?
                            <div className="rfp_submit_form_item" style={{ display: "flex", justifyContent: "flex-end" }}>
                                <IonButton
                                    fill="clear"
                                    className={"delete_member_in_ev_b"}
                                    onClick={() => removeMember(idx)}
                                    disabled={members.length === 1}
                                >
                                    <IonIcon slot="start" icon={trashOutline} />
                                    Remove
                                </IonButton>
                            </div>
                            : ''
                        }

                        <hr className="rfp_member_divider" />
                    </div>
                ))}

                <div className="rfp_submit_form_item" style={{ display: "flex", justifyContent: "flex-start" }}>
                    <IonButton fill="clear" onClick={addMember}>
                        <IonIcon slot="start" icon={addCircleOutline} />
                        ADD MEMBER
                    </IonButton>
                </div>
            </IonContent>

            <IonFooter className="job_detail_sheet_footer ion-padding">
                <IonToolbar>
                    <IonRow>
                        <IonCol>
                            Total Price ₹{totalPriceToPay}
                        </IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol>
                            <IonButton className="close_job_detail_button" expand="block" onClick={onDismiss}>
                                Close
                            </IonButton>
                        </IonCol>
                        <IonCol>
                            <IonButton className="apply_job_button" expand="block" onClick={handleSubmit}>
                                Submit
                            </IonButton>
                        </IonCol>
                    </IonRow>
                </IonToolbar>
            </IonFooter>
        </IonModal>
    );
}
